<template>
    <RegularModal ref="modal" @closed="onClosed">
        <template v-slot:title>
            {{
                isMultiBranch
                    ? translate("page.category_manager.append_branch_multi_title")
                    : translate("page.category_manager.append_branch_title")
            }}
        </template>
        <template v-slot:body>
            <Notification class="is-info is-light mb-4" :centered="false">
                <span class="icon"><i class="fas fa-info-circle"></i></span>
                {{ translate("page.category_manager.append_branch_info", false, [store.totalCategoryCount, rootName]) }}
            </Notification>

            <BranchNameField
                ref="branchNameField"
                v-model="branchName"
                :new-branch-count="categoryCount"
                :branches="branches"
                :show-error="showValidationError"
            />
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton class="button is-success" :is-loading="isLoading" @click="confirm">
                    {{
                        isMultiBranch
                            ? translate("page.category_manager.append_branch_multi_confirm")
                            : translate("page.category_manager.append_branch_confirm")
                    }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, nextTick, ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import Notification from "../Common/Notification.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { translate } from "../Common/translations";
    import { useModal } from "../Common/useModal";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import BranchNameField from "./BranchNameField.vue";
    import { BRANCH_NAME_MAX_LENGTH } from "./branchName";

    const store = useCategoriesManagerStore();

    defineProps<{
        categoryCount: number;
        isLoading: boolean;
    }>();

    const emit = defineEmits<{
        confirm: [branchName: string];
    }>();

    const { modal, open: openModal, close } = useModal();
    const branchNameField = ref<{ errorKey: string | null; focus: () => void } | null>(null);
    const branchName = ref("");
    const branches = ref<{ name: string; count: number }[]>([]);
    const showValidationError = ref(false);
    const rootName = ref("");

    const isMultiBranch = computed(() => branches.value.length > 0);

    function open(prefill: string) {
        branches.value = [];
        branchName.value = prefill.slice(0, BRANCH_NAME_MAX_LENGTH);
        showValidationError.value = false;
        rootName.value = store.rootNode?.name ?? "";
        openModal();
        nextTick(() => branchNameField.value?.focus());
    }

    function openForMultipleBranches(branchList: { name: string; count: number }[]) {
        branches.value = branchList;
        branchName.value = "";
        showValidationError.value = false;
        rootName.value = store.rootNode?.name ?? "";
        openModal();
    }

    function confirm() {
        if (isMultiBranch.value) {
            emit("confirm", branches.value.map(branch => branch.name).join(" + "));
            return;
        }

        if (branchNameField.value && branchNameField.value.errorKey !== null) {
            showValidationError.value = true;
            return;
        }
        emit("confirm", branchName.value.trim());
    }

    function onClosed() {
        showValidationError.value = false;
    }

    defineExpose({ open, openForMultipleBranches, close });
</script>
